/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {debounce} from 'lodash';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {Preference} from '@ion/core/src/types/preferences';
import {notDeepEqual} from '@ion/core/src/utils/Equality';
import {ReportPreferences} from '@/store/reducers/reports';

/**
 * Merges the given settings over those already persisted, and saves the
 * result when it differs from what was previously stored.
 *
 * A caller which owns a single setting (e.g. a control that only ever
 * changes `showStatusEvents`) can invoke this directly for an immediate,
 * synchronous save without going through the debounced `savePreferences`
 * below - sharing that debounced instance's single pending-call slot would
 * risk discarding another caller's still-pending, unrelated update.
 *
 *  ```typescript
 *
 *    import {persistPreferences} from '../helpers';
 *
 *    persistPreferences({showStatusEvents: true});
 *
 * ```
 *
 */
export const persistPreferences = (current: Partial<ReportPreferences>) => {
  const previous = UserPreferences.get(Preference.trackingAssetHistory);
  // the given settings are merged over those already persisted so that a caller
  // may save a single setting without discarding the remainder, and so that a
  // setting which has yet to be restored is not overwritten by the transient
  // state a component holds before it has been applied ...
  const updated = {...previous as ReportPreferences, ...current};
  UserPreferences.set(Preference.trackingAssetHistory, updated);
  if (notDeepEqual(previous, updated)) {
    UserPreferences.save();
  }
};

/**
 * The debounced utility for saving updated preferences. Intended for a
 * caller (such as the Asset History page itself) which reports its full,
 * current settings on every relevant change and can tolerate batching
 * rapid, successive saves together.
 *
 *  ```typescript
 *
 *    import {savePreferences} from '../helpers';
 *
 *   const current: ReportPreferences = {
 *     showSummaryCards,
 *     showTripVisible,
 *     showEventsBetweenTrips,
 *     showTotalAssets,
 *     showTotalEvents
 *   };
 *   savePreferences(current);
 *
 * ```
 *
 */
export const savePreferences = debounce(persistPreferences, 1000, {maxWait: 0});

/**
 * The utility function for getting assetHistorySettings based on user preferences.
 *
 *  ```typescript
 *
 *    import {getAssetHistorySetting} from '../helpers';
 *
 *    const assetHistorySettings = getAssetHistorySetting();
 *
 * ```
 *
 */
export const getAssetHistorySetting = (): ReportPreferences => {
  return UserPreferences.get(Preference.trackingAssetHistory);
};
